import { useState } from 'react';
import { DocumentArrowUpIcon, SparklesIcon } from '@heroicons/react/24/outline';

interface DocumentUploadProps {
  onExtract: (text: string) => void;
  extracting: boolean;
  disabled: boolean;
}

export function DocumentUpload({ onExtract, extracting, disabled }: DocumentUploadProps) {
  const [textContent, setTextContent] = useState('');
  const [mode, setMode] = useState<'paste' | 'upload'>('paste');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (textContent.trim()) {
      onExtract(textContent);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      setTextContent(text);
    };
    reader.readAsText(file);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Upload Document</h2>

      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setMode('paste')}
          className={`flex-1 py-2 px-4 rounded-md font-medium ${
            mode === 'paste'
              ? 'bg-primary-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Paste Text
        </button>
        <button
          onClick={() => setMode('upload')}
          className={`flex-1 py-2 px-4 rounded-md font-medium ${
            mode === 'upload'
              ? 'bg-primary-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Upload File
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {mode === 'paste' ? (
          <div>
            <label htmlFor="textContent" className="block text-sm font-medium text-gray-700 mb-2">
              Paste contract text
            </label>
            <textarea
              id="textContent"
              value={textContent}
              onChange={(e) => setTextContent(e.target.value)}
              rows={12}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 font-mono text-sm"
              placeholder="Paste the contract text here..."
              disabled={disabled}
            />
          </div>
        ) : (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload document file
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-primary-500 transition-colors">
              <DocumentArrowUpIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <input
                type="file"
                accept=".txt,.doc,.docx,.pdf"
                onChange={handleFileUpload}
                className="hidden"
                id="file-upload"
                disabled={disabled}
              />
              <label
                htmlFor="file-upload"
                className="cursor-pointer text-primary-600 hover:text-primary-700 font-medium"
              >
                Choose a file
              </label>
              <p className="mt-2 text-sm text-gray-500">
                or drag and drop
              </p>
              <p className="mt-1 text-xs text-gray-400">
                TXT, DOC, DOCX, PDF up to 10MB
              </p>
            </div>
            {textContent && (
              <div className="mt-3 p-3 bg-gray-50 rounded-md">
                <p className="text-sm text-gray-700">
                  File loaded: {textContent.length} characters
                </p>
              </div>
            )}
          </div>
        )}

        <button
          type="submit"
          disabled={!textContent.trim() || disabled}
          className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
        >
          {extracting ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              Extracting...
            </>
          ) : (
            <>
              <SparklesIcon className="w-5 h-5" />
              Extract with AI
            </>
          )}
        </button>
      </form>
    </div>
  );
}

